import { FC, Fragment } from 'react';
import { useForm, Controller } from 'react-hook-form';
// Redux
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Models
import IBillingInvoice from 'app/models/BillingInvoice';
// Async
import { batchChargeInvoices, chargeInvoice } from 'app/store/BillingCharges/BillingCharges.async';
// Selectors
import { selectBatchSentInvoices } from 'app/store/BillingInvoices/BillingInvoices.selectors';
import { selectLoading } from 'app/store/BillingCharges/BillingCharges.selectors';
// Mui
import { Box, Divider, Typography } from '@mui/material';
// Components
import Dialog from 'app/components/Dialog';
import { Input, Select, Button, Checkbox } from 'app/components/Mui';
// Utilities
import { isRequired } from 'app/utilities/Validations';
// i18next
import { useTranslation } from 'react-i18next';
// Mixpanel
import MixpanelActions from 'app/features/mixpanel/MixpanelActions';
// Utilities
import { toNumberWithCommas } from 'app/utilities/Utilities';

interface IFormData {
  accountNumber: string;
  accountType: string;
  city: string;
  line: string;
  state: string;
  zipCode: string;
  checkName: string;
  checkType: string;
  routingNumber: string;
  acceptTheTermsAndConditions: boolean;
}

type Props = {
  open: boolean;
  onClose: () => void;
  invoice?: IBillingInvoice;
}

const ACHPaymentFormDialog:FC<Props> = ({
  // Props
  open, onClose, invoice
}) => {
  const { t } = useTranslation();
  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const batchInvoices = useAppSelector(selectBatchSentInvoices);
  const loading = useAppSelector(selectLoading);
  
  const { control, handleSubmit, formState:{ errors } } = useForm<IFormData>({
    defaultValues: {
      accountNumber: '',
      accountType: '',
      city: '',
      line: '',
      state: '',
      zipCode: '',
      checkName: '',
      checkType: '',
      routingNumber: '',
      acceptTheTermsAndConditions: false
    }
  });

  const onSubmit = handleSubmit(async (data:IFormData) => {
    const { city, line, state, zipCode, ...otherData } = data;
    const check = {
      ...otherData,
      billingAddress: { city, line, state, zipCode }
    };
    try {
      if ( invoice ){
        await dispatch(chargeInvoice({
          check,
          invoiceId: invoice.id
        })).unwrap();
        dispatch(MixpanelActions.handleBillingInvoicesACHPaymentClick({ invoiceId: invoice.id }));
      } else {
        const batchInvoicesIds = batchInvoices.map((inv) => inv.id);
        await dispatch(batchChargeInvoices({
          check,
          invoiceIds: batchInvoicesIds
        })).unwrap();
        dispatch(MixpanelActions.handleBillingInvoicesBulkACHPaymentClick({ invoicesIds: batchInvoicesIds }));
      }
      onClose();
    } catch(error){}
  });

  const accountTypeOptions = [
    { id: '', name: t('pages.billingAndSubscriptions.chooseAccountType') },
    { id: 'checking', name: t('pages.billingAndSubscriptions.checking') },
    { id: 'savings', name: t('pages.billingAndSubscriptions.savings') },
  ];

  const checkTypeOptions = [
    { id: '', name: t('pages.billingAndSubscriptions.chooseRoutingType') },
    { id: 'business', name: t('pages.billingAndSubscriptions.business') },
    { id: 'personal', name: t('pages.billingAndSubscriptions.personal') },
  ];

  const amountPay = invoice ? invoice.amount : batchInvoices.reduce((total, inv) => total + (inv.amount || 0), 0);

  const actions = (
    <Fragment>
      <Button
        name="Cancel ACH payment dialog"
        onClick={onClose}
      >{t('buttons.close')}</Button>
      <Button
        name="Pay ACH payment dialog"
        loading={loading}
        onClick={onSubmit}
        variant="contained"
        color="primary"
      >{`${t('buttons.pay')} $${toNumberWithCommas(amountPay)}`}</Button>
    </Fragment>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      title={t('pages.billingAndSubscriptions.achPaymentFormTitle')}
      actions={actions}

      PaperProps={{
        component: 'form',
        onSubmit,
        noValidate: true
      }}
    >
      <Controller
        control={control} name="accountNumber"
        rules={{ required: isRequired }}
        render={({ field }) => (
          <Input
            {...field}
            label={t('pages.billingAndSubscriptions.accountNumber')}
            error={Boolean(errors.accountNumber)}
            helperText={errors.accountNumber?.message || ''}
            required
          />
        )}
      />
      <Controller
        control={control} name="accountType"
        rules={{ required: isRequired }}
        render={({ field }) => (
          <Select
            {...field}
            label={t('pages.billingAndSubscriptions.accountType')}
            options={accountTypeOptions}
            error={Boolean(errors.accountType)}
            helperText={errors.accountType?.message || ''}
            required
          />
        )}
      />
      <Controller
        control={control} name="line"
        rules={{ required: isRequired }}
        render={({ field }) => (
          <Input
            {...field}
            label={t('labels.address')}
            error={Boolean(errors.line)}
            helperText={errors.line?.message || ''}
            required
          />
        )}
      />
      <Box sx={{ display: 'flex', gap: 4 }}>
        <Controller
          control={control} name="city"
          rules={{ required: isRequired }}
          render={({ field }) => (
            <Input
              {...field}
              label={t('labels.city')}
              error={Boolean(errors.city)}
              helperText={errors.city?.message || ''}
              required
            />
          )}
        />
        <Controller
          control={control} name="state"
          rules={{ required: isRequired }}
          render={({ field }) => (
            <Input
              {...field}
              label={t('labels.state')}
              error={Boolean(errors.state)}
              helperText={errors.state?.message || ''}
              required
            />
          )}
        />
        <Controller
          control={control} name="zipCode"
          rules={{ required: isRequired }}
          render={({ field }) => (
            <Input
              {...field}
              label={t('labels.zipCode')}
              error={Boolean(errors.zipCode)}
              helperText={errors.zipCode?.message || ''}
              required
            />
          )}
        />
      </Box>
      <Controller
        control={control} name="checkName"
        rules={{ required: isRequired }}
        render={({ field }) => (
          <Input
            {...field}
            label={t('pages.billingAndSubscriptions.accountHolderName')}
            error={Boolean(errors.checkName)}
            helperText={errors.checkName?.message || ''}
            required
          />
        )}
      />
      <Controller
        control={control} name="checkType"
        rules={{ required: isRequired }}
        render={({ field }) => (
          <Select
            {...field}
            label={t('pages.billingAndSubscriptions.checkType')}
            options={checkTypeOptions}
            error={Boolean(errors.checkType)}
            helperText={errors.checkType?.message || ''}
            required
          />
        )}
      />
      <Controller
        control={control} name="routingNumber"
        rules={{ required: isRequired }}
        render={({ field }) => (
          <Input
            {...field}
            label={t('pages.billingAndSubscriptions.routingNumber')}
            error={Boolean(errors.routingNumber)}
            helperText={errors.routingNumber?.message || ''}
            required
          />
        )}
      />
      <Divider sx={{ my: 1 }} />
      <Box sx={{ maxHeight: 200, overflowY: 'scroll'}}>
        <Typography variant="h6">{t('pages.billingAndSubscriptions.paymentTermAndConditions')}</Typography>
        <Typography variant="button">{t('pages.billingAndSubscriptions.authorization')}</Typography>
        <Typography variant="body2">{t('pages.billingAndSubscriptions.authorizationContent')}</Typography>
        <Typography variant="button">{t('pages.billingAndSubscriptions.receipt')}</Typography>
        <Typography variant="body2">{t('pages.billingAndSubscriptions.receiptContent')}</Typography>
        <Typography variant="button">{t('pages.billingAndSubscriptions.contact')}</Typography>
        <Typography variant="body2">{t('pages.billingAndSubscriptions.contactContent')}</Typography>
      </Box>
      
      <Controller
        control={control} name="acceptTheTermsAndConditions"
        rules={{ required: isRequired }}
        render={({ field:{ onChange, ...otherField }}) => (
          <Checkbox
            {...otherField}
            onChange={(_, checked:boolean) => onChange(checked)}
            label={t('pages.billingAndSubscriptions.acceptTermsAndConditions')}
            error={Boolean(errors.acceptTheTermsAndConditions)}
            helperText={errors.acceptTheTermsAndConditions?.message || ''}
            required
          />
        )}
      />
    </Dialog>
  )
}

export default ACHPaymentFormDialog;
